import React, { useState, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

import { supabase } from "../../supabaseClient";
import { useTournamentDocuments } from "../../hooks/useTournamentDocuments";
import Breadcrumb from "../../components/Breadcrumb/Breadcrumb";
import SEOHead from "../../components/SEO/SEOHead";
import {
  generateTournamentStructuredData,
} from "../../components/SEO/StructuredData";

import TournamentHeader from "../../components/Tournament/TournamentHeader";
import TournamentOverviewBoard from "../../components/Tournament/TournamentOverviewBoard";
import TournamentEvents from "../../components/Tournament/TournamentEvents";
import TournamentSquadsOverview from "../../components/Tournament/TournamentSquadsOverview";
import TournamentLocations from "../../components/Tournament/TournamentLocations";
import TournamentProgressSidebar from "../../components/Tournament/TournamentProgressSidebar";
import TournamentDocumentsSidebar from "../../components/Tournament/TournamentDocumentsSidebar";
import TournamentContactInfo from "../../components/Tournament/TournamentContactInfo";
import SignupPrompt from "../../components/Tournament/SignupPrompt";
import {
  listDistinctSquadBowlingCenters,
  locationRowsFromCenters,
} from "../../utility/organizationBowlingCenters";
import { TournamentPageSkeleton } from "../../components/UI/DesignStandards";
import { useTournamentId, useTournamentSlug } from "../../context/TournamentRouteContext";
import { tournamentPath } from "../../utility/tournamentUrls";
import { prerenderTournamentForPath } from "../../prerender/pageData";

export default function Tournament() {
  const tournamentId = useTournamentId();
  const tournamentSlug = useTournamentSlug();
  const location = useLocation();
  const { getPublicDocuments } = useTournamentDocuments(tournamentId);
  const seeded = prerenderTournamentForPath(location.pathname);
  const hadTournament = useRef(Boolean(seeded?.tournament));

  const [tournamentData, setTournamentData] = useState(
    () => seeded?.tournament ?? null
  );
  const [tournamentLocations, setTournamentLocations] = useState(
    () => seeded?.locations ?? []
  );
  const [squadDates, setSquadDates] = useState(() => seeded?.squadDates ?? []);
  const [loading, setLoading] = useState(() => !seeded?.tournament);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const fetchTournamentData = async () => {
      try {
        const { data: tournament, error: tournamentError } = await supabase
          .from("tournaments")
          .select("*")
          .eq("id", tournamentId)
          .single();

        if (tournamentError) throw tournamentError;

        const [{ data: squadRows, error: squadError }, centers] =
          await Promise.all([
            supabase
              .from("squads")
              .select("squadDate")
              .eq("tournamentID", tournamentId)
              .eq("isDeleted", false),
            listDistinctSquadBowlingCenters(tournamentId).catch(
              (locationsError) => {
                console.error(
                  "Error fetching tournament locations:",
                  locationsError
                );
                return [];
              }
            ),
          ]);

        if (squadError) {
          console.error("Error fetching squad dates:", squadError);
        }

        if (cancelled) return;
        hadTournament.current = true;
        setTournamentLocations(locationRowsFromCenters(centers));
        setSquadDates(
          [...new Set((squadRows || []).map((row) => row.squadDate).filter(Boolean))]
        );
        setTournamentData((prev) => ({
          ...tournament,
          ...(prev?.id === tournament.id && prev.organizations
            ? { organizations: prev.organizations }
            : {}),
          ...(prev?.id === tournament.id && prev.entryFees?.length
            ? { entryFees: prev.entryFees }
            : {}),
        }));
        setError(null);
      } catch (err) {
        console.error("Error fetching tournament data:", err);
        if (!cancelled && !hadTournament.current) {
          setError("Failed to load tournament data");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchTournamentData();
    return () => {
      cancelled = true;
    };
  }, [tournamentId]);

  const openDocument = (url) => {
    const googleDocsUrl = `https://docs.google.com/viewer?url=${encodeURIComponent(
      url
    )}&embedded=true`;
    window.open(googleDocsUrl, "_blank", "noopener,noreferrer");
  };

  const handleNameUpdate = (newName) => {
    setTournamentData((prev) => ({ ...prev, name: newName }));
  };
  const handleDateUpdate = (newDate) => {
    setTournamentData((prev) => ({ ...prev, startDate: newDate }));
  };

  if (error) {
    return <div className="text-red-500 text-center">{error}</div>;
  }

  if (loading || !tournamentData) {
    return <TournamentPageSkeleton layout="overview" />;
  }

  const breadcrumbItems = [
    { label: "Home", path: "/" },
    { label: "Tournaments", path: "/tournaments" },
    { label: tournamentData.name || "Tournament" },
  ];

  const publicDocuments = getPublicDocuments();
  const houses = tournamentLocations
    .map((location) => location.bowlingCenters)
    .filter(Boolean);

  return (
    <>
      <SEOHead
        title={`${tournamentData.name} - Bowling Tournament`}
        description={
          tournamentData.description ||
          `Join ${tournamentData.name} bowling tournament.`
        }
        keywords={[
          tournamentData.name,
          "bowling tournament",
          "tournament registration",
          "bowling competition",
          tournamentData.organizations?.name || "bowling league",
          "tournament results",
          "bowling event",
        ]}
        canonicalUrl={tournamentPath(tournamentSlug)}
        ogType="sports_event"
        structuredData={generateTournamentStructuredData({
          ...tournamentData,
          slug: tournamentSlug,
          locations: tournamentLocations,
        })}
      />
      <div className="flex justify-center mb-2">
        <Breadcrumb items={breadcrumbItems} />
      </div>

      <TournamentHeader
        tournamentData={{
          ...tournamentData,
          onNameUpdate: handleNameUpdate,
          onDateUpdate: handleDateUpdate,
        }}
        tournamentId={tournamentId}
        squadDates={squadDates}
        houses={houses}
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <SignupPrompt tournamentId={tournamentId} />
          <TournamentOverviewBoard tournamentId={tournamentId} />
          <TournamentSquadsOverview tournamentId={tournamentId} />
          <TournamentEvents
            tournamentId={tournamentId}
            tournamentData={tournamentData}
          />
          <TournamentLocations tournamentLocations={tournamentLocations} />
        </div>

        <div className="space-y-6">
          <TournamentProgressSidebar tournamentId={tournamentId} />
          <TournamentContactInfo tournamentId={tournamentId} />
          <TournamentDocumentsSidebar
            documents={publicDocuments}
            onDocumentClick={openDocument}
          />
        </div>
      </div>
    </>
  );
}
